import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { IconPlayerPlay } from "@tabler/icons-react";
import type { FC } from "react";
import { PlaybookSaveStatusIndicator } from "../../components/PlaybookSaveStatusIndicator.js";
import { PlaybookStatusPill } from "../../components/PlaybookStatusPill.js";
import { PublishButton } from "./publishButton.js";
import { TestStatusPill } from "./testStatusPill.js";
import type { TeammateAgentConfigController } from "./useTeammateAgentConfigController.js";
import { VersionHistoryButton } from "./versionHistoryButton.js";

export interface AgentConfigToolbarProps {
  controller: TeammateAgentConfigController;
}

/**
 * The shared agent-config toolbar: "Try this agent", test/status pills, save
 * status, discard, publish, and version history. Rendered by both the legacy
 * Configuration tab and the static agent layout so the two behave identically.
 */
export const AgentConfigToolbar: FC<AgentConfigToolbarProps> = ({
  controller,
}) => {
  const {
    teammateAgentId,
    playbookId,
    agentConfig,
    playbook,
    isPreparingAgent,
    handleOpenSimulation,
    hasUnpublishedChanges,
    saveStatus,
    onDiscardChanges,
    isDiscarding,
    isActionInProgress,
    publishableChanges,
    isPublishing,
    onPublish,
  } = controller;

  return (
    <div className="flex items-center justify-between border-b border-border-light bg-card px-6 py-3">
      <div className="flex items-center gap-3">
        <Button
          variant={ButtonVariant.Secondary}
          onClick={handleOpenSimulation}
          icon={<IconPlayerPlay className="h-4 w-4" />}
          isLoading={isPreparingAgent}
          size={Size.MEDIUM}
          isDisabled={isPreparingAgent}
        >
          {isPreparingAgent ? "Preparing agent..." : "Try this agent"}
        </Button>
        <TestStatusPill
          teammateAgentId={teammateAgentId}
          playbookId={playbookId}
          agentConfigDraftRevisionId={agentConfig.draftRevisionId}
          playbookDraftRevisionId={playbook.draftRevisionId}
          agentConfigHasUnsavedChanges={agentConfig.hasUnsavedChanges}
          playbookHasUnsavedChanges={playbook.hasUnsavedChanges}
          onAgentConfigSaveDraft={agentConfig.onSaveDraft}
          onPlaybookSaveDraft={playbook.onSaveDraft}
        />
        {/* Status indicators */}
        <PlaybookStatusPill isLive={!hasUnpublishedChanges} />
        <PlaybookSaveStatusIndicator status={saveStatus} />
      </div>
      <div className="flex items-center gap-2">
        {hasUnpublishedChanges && (
          <Button
            variant={ButtonVariant.Secondary}
            onClick={onDiscardChanges}
            isDisabled={isActionInProgress}
            isLoading={isDiscarding}
            size={Size.MEDIUM}
          >
            Discard changes
          </Button>
        )}
        <PublishButton
          changes={publishableChanges}
          hasChanges={hasUnpublishedChanges}
          isDisabled={isActionInProgress}
          isLoading={isPublishing}
          blockedReason={
            agentConfig.publishBlockedReason ?? playbook.publishBlockedReason
          }
          onPublish={onPublish}
        />
        <VersionHistoryButton
          teammateAgentId={teammateAgentId}
          isDisabled={isActionInProgress}
        />
      </div>
    </div>
  );
};
